import { GROUND_Y, JUMP_GRADE_POINTS, JUMP_ZONE, SIM_HZ, type JumpGrade } from '../../data/tuning';
import { stepPlayer, type PlayerState } from '../physics';

/**
 * El Salto (GDD §11.1-11.3, issue #93): the pilot jumps with La Bolsa from the plane, falls with the flight
 * gravity, opens the parachute 0.5 s later, descends at 2 u/s while moving forward at 3 u/s that brakes at
 * 1 u/s², and lands on the ground line (y = 1). Pure and deterministic.
 */
export type JumpPhase = 'falling' | 'parachute' | 'landed';

export interface JumpState {
  phase: JumpPhase;
  /** Ticks since the jump. */
  ticks: number;
  /** Horizontal speed of the pilot relative to the ground (u/s). */
  vx: number;
  /** The plane did not wait for the button: it reached the right edge (§11.3). */
  forced: boolean;
  /** Where the pilot landed, once landed. */
  landingX: number | null;
}

export const PARACHUTE_TICKS = Math.round(JUMP_ZONE.parachuteDelaySeconds * SIM_HZ);

export const startJump = (forced: boolean): JumpState => ({
  phase: 'falling',
  ticks: 0,
  vx: JUMP_ZONE.jumpHorizontalSpeed,
  forced,
  landingX: null,
});

/** One tick of the pilot: returns where it is now. The state records the phase and the landing. */
export function stepPilot(jump: JumpState, pilot: PlayerState): PlayerState {
  if (jump.phase === 'landed') return pilot;
  jump.ticks += 1;
  const x = pilot.x + jump.vx / SIM_HZ;
  jump.vx = Math.max(0, jump.vx - JUMP_ZONE.jumpHorizontalDecel / SIM_HZ);
  let next: PlayerState;
  if (jump.phase === 'falling') {
    // The first 0.5 s fall with the flight gravity; the parachute opens at the end of the 30th tick.
    next = { ...stepPlayer(pilot, { flap: false }), x };
    if (jump.ticks >= PARACHUTE_TICKS) {
      jump.phase = 'parachute';
      next = { ...next, vy: JUMP_ZONE.parachuteDescent };
    }
  } else {
    const vy = JUMP_ZONE.parachuteDescent;
    next = { x, y: pilot.y + vy / SIM_HZ, vy };
  }
  if (next.y <= GROUND_Y) {
    jump.phase = 'landed';
    jump.landingX = next.x;
    return { x: next.x, y: GROUND_Y, vy: 0 };
  }
  return next;
}

/** Where a jump from (x, y) lands, simulating the same steps (for the scenarios and the render of #94). */
export function predictLanding(x: number, y: number, forced = false): number {
  const jump = startJump(forced);
  let pilot: PlayerState = { x, y, vy: 0 };
  for (let i = 0; i < 100 * SIM_HZ && jump.phase !== 'landed'; i++) pilot = stepPilot(jump, pilot);
  return jump.landingX ?? pilot.x;
}

/** The bonus table of §11.2 as data: inclusive thresholds; anything farther, or a forced jump, is FORZOSO. */
export const JUMP_GRADES: readonly { maxDistance: number; grade: JumpGrade }[] = [
  { maxDistance: 0.5, grade: 'PERFECTO' },
  { maxDistance: 1.5, grade: 'EXCELENTE' },
  { maxDistance: 3.0, grade: 'BUENO' },
  { maxDistance: 6.0, grade: 'REGULAR' },
];

export function gradeJump(distance: number, forced: boolean): { grade: JumpGrade; bonus: number } {
  const grade = forced ? 'FORZOSO' : (JUMP_GRADES.find((row) => distance <= row.maxDistance + 1e-9)?.grade ?? 'FORZOSO');
  return { grade, bonus: JUMP_GRADE_POINTS[grade] };
}

/** Order of the grades for the best jump of a level: PERFECTO > EXCELENTE > BUENO > REGULAR > FORZOSO. */
const RANK: Readonly<Record<JumpGrade, number>> = { PERFECTO: 5, EXCELENTE: 4, BUENO: 3, REGULAR: 2, FORZOSO: 1 };

export const betterJump = (a: JumpGrade | null, b: JumpGrade | null): JumpGrade | null =>
  !a ? b : !b ? a : RANK[b] > RANK[a] ? b : a;
